(function () {
  'use strict';

  let dialog;
  let viewport;
  let zoomImage;
  let dots;
  let previousButton;
  let nextButton;
  let closeButton;
  let viewerState;

  const script = document.currentScript;
  const labels = {
    open: script?.dataset.zoomOpenLabel,
    dialog: script?.dataset.zoomDialogLabel,
    close: script?.dataset.zoomCloseLabel,
    previous: script?.dataset.zoomPreviousLabel,
    next: script?.dataset.zoomNextLabel,
    dot: script?.dataset.zoomDotLabel,
  };

  function queryAll(root, selector) {
    return Array.from(root.querySelectorAll(selector));
  }

  function applyTransform() {
    zoomImage.style.transform =
      `translate(${viewerState.translateX}px, ${viewerState.translateY}px) scale(${viewerState.scale})`;
  }

  function resetTransform() {
    viewerState.scale = 1;
    viewerState.translateX = 0;
    viewerState.translateY = 0;
    applyTransform();
  }

  function clampPan() {
    const viewportRect = viewport.getBoundingClientRect();
    const maxTranslateX = Math.max(
      0,
      (zoomImage.clientWidth * viewerState.scale - viewportRect.width) / 2,
    );
    const maxTranslateY = Math.max(
      0,
      (zoomImage.clientHeight * viewerState.scale - viewportRect.height) / 2,
    );

    viewerState.translateX = Math.min(
      maxTranslateX,
      Math.max(-maxTranslateX, viewerState.translateX),
    );
    viewerState.translateY = Math.min(
      maxTranslateY,
      Math.max(-maxTranslateY, viewerState.translateY),
    );
  }

  function zoomAt(clientX, clientY, nextScale) {
    const viewportRect = viewport.getBoundingClientRect();
    const anchorX = clientX - (viewportRect.left + viewportRect.width / 2);
    const anchorY = clientY - (viewportRect.top + viewportRect.height / 2);
    const scaleRatio = nextScale / viewerState.scale;

    viewerState.translateX =
      anchorX - (anchorX - viewerState.translateX) * scaleRatio;
    viewerState.translateY =
      anchorY - (anchorY - viewerState.translateY) * scaleRatio;
    viewerState.scale = nextScale;
    clampPan();
    applyTransform();
  }

  function renderDots() {
    dots.replaceChildren();
    if (!viewerState || viewerState.images.length < 2) return;

    for (let index = 0; index < viewerState.images.length; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `pdp-zoom__dot${index === viewerState.index ? ' is-active' : ''}`;
      dot.setAttribute(
        'aria-label',
        labels.dot.replace('__INDEX__', String(index + 1)),
      );
      if (index === viewerState.index) dot.setAttribute('aria-current', 'true');
      dot.addEventListener('click', () => showImage(index));
      dots.appendChild(dot);
    }
  }

  function syncSourceGallery() {
    if (!viewerState?.gallery) return;
    const activeIndex = String(viewerState.index);

    queryAll(
      viewerState.gallery,
      '.pdp-gallery__slide, .pf-gallery__slide',
    ).forEach((slide) => {
      slide.classList.toggle(
        'is-active',
        slide.getAttribute('data-slide') === activeIndex,
      );
    });

    queryAll(
      viewerState.gallery,
      '.pdp-gallery__thumb, .pf-gallery__thumb',
    ).forEach((thumbnail) => {
      thumbnail.classList.toggle(
        'is-active',
        thumbnail.getAttribute('data-thumb') === activeIndex,
      );
    });
  }

  function renderImage() {
    const selectedImage = viewerState.images[viewerState.index];
    zoomImage.src = selectedImage.src;
    zoomImage.alt = selectedImage.alt;
    resetTransform();
    renderDots();

    const hasMultipleImages = viewerState.images.length > 1;
    previousButton.hidden = !hasMultipleImages;
    nextButton.hidden = !hasMultipleImages;
  }

  function showImage(index) {
    if (!viewerState) return;
    const imageCount = viewerState.images.length;
    viewerState.index = (index + imageCount) % imageCount;
    renderImage();
    syncSourceGallery();
  }

  function step(delta) {
    if (viewerState) showImage(viewerState.index + delta);
  }

  function closeZoom() {
    if (!dialog) return;
    if (typeof dialog.close === 'function') {
      dialog.close();
      return;
    }

    dialog.removeAttribute('open');
    document.body.classList.remove('pdp-zoom-locked');
    viewerState = undefined;
  }

  function distance(firstPoint, secondPoint) {
    return Math.hypot(
      firstPoint.x - secondPoint.x,
      firstPoint.y - secondPoint.y,
    );
  }

  function midpoint(firstPoint, secondPoint) {
    return {
      x: (firstPoint.x + secondPoint.x) / 2,
      y: (firstPoint.y + secondPoint.y) / 2,
    };
  }

  function wireGestures() {
    const pointers = new Map();
    const pointerOrder = [];
    let pinchStartDistance = 0;
    let pinchStartScale = 1;
    let panStart;
    let gestureStart;
    let lastTap = { time: 0, x: 0, y: 0 };

    function handleTap(clientX, clientY) {
      const currentTime = Date.now();
      const isDoubleTap =
        currentTime - lastTap.time < 300 &&
        Math.hypot(clientX - lastTap.x, clientY - lastTap.y) < 30;

      if (isDoubleTap) {
        lastTap.time = 0;
        if (viewerState.scale > 1) resetTransform();
        else zoomAt(clientX, clientY, 2.4);
        return;
      }

      lastTap = { time: currentTime, x: clientX, y: clientY };
    }

    viewport.addEventListener('pointerdown', (event) => {
      if (!viewerState) return;

      viewport.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      pointerOrder.push(event.pointerId);

      if (pointerOrder.length === 1) {
        gestureStart = {
          x: event.clientX,
          y: event.clientY,
          time: Date.now(),
          multi: false,
        };
        panStart = {
          translateX: viewerState.translateX,
          translateY: viewerState.translateY,
          x: event.clientX,
          y: event.clientY,
        };
      } else if (pointerOrder.length === 2) {
        gestureStart.multi = true;
        pinchStartDistance = distance(
          pointers.get(pointerOrder[0]),
          pointers.get(pointerOrder[1]),
        );
        pinchStartScale = viewerState.scale;
      }
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!viewerState || !pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (pointerOrder.length === 2) {
        const firstPoint = pointers.get(pointerOrder[0]);
        const secondPoint = pointers.get(pointerOrder[1]);
        if (pinchStartDistance > 0) {
          const nextScale = Math.min(
            4,
            Math.max(
              1,
              pinchStartScale *
                (distance(firstPoint, secondPoint) / pinchStartDistance),
            ),
          );
          const center = midpoint(firstPoint, secondPoint);
          zoomAt(center.x, center.y, nextScale);
        }
      } else if (pointerOrder.length === 1 && panStart && viewerState.scale > 1) {
        viewerState.translateX =
          panStart.translateX + event.clientX - panStart.x;
        viewerState.translateY =
          panStart.translateY + event.clientY - panStart.y;
        clampPan();
        applyTransform();
      }
    });

    function endPointer(event) {
      if (!pointers.has(event.pointerId)) return;
      pointers.delete(event.pointerId);
      const pointerIndex = pointerOrder.indexOf(event.pointerId);
      if (pointerIndex !== -1) pointerOrder.splice(pointerIndex, 1);

      if (pointerOrder.length !== 0 || !viewerState) {
        if (pointerOrder.length === 1) {
          const remainingPoint = pointers.get(pointerOrder[0]);
          panStart = {
            translateX: viewerState.translateX,
            translateY: viewerState.translateY,
            x: remainingPoint.x,
            y: remainingPoint.y,
          };
          gestureStart = {
            x: remainingPoint.x,
            y: remainingPoint.y,
            time: Date.now(),
            multi: true,
          };
          pinchStartDistance = 0;
        }
        return;
      }

      if (!gestureStart.multi) {
        const deltaX = event.clientX - gestureStart.x;
        const deltaY = event.clientY - gestureStart.y;
        const elapsedTime = Date.now() - gestureStart.time;
        const movement = Math.hypot(deltaX, deltaY);

        if (movement < 12 && elapsedTime < 500) {
          handleTap(event.clientX, event.clientY);
        } else if (viewerState.scale === 1 && movement >= 40) {
          if (Math.abs(deltaX) > Math.abs(deltaY) * 1.3) {
            step(deltaX < 0 ? 1 : -1);
          } else if (deltaY > 90) {
            closeZoom();
          }
        }
      }

      gestureStart = undefined;
      panStart = undefined;
      pinchStartDistance = 0;
    }

    viewport.addEventListener('pointerup', endPointer);
    viewport.addEventListener('pointercancel', endPointer);
    viewport.addEventListener(
      'wheel',
      (event) => {
        if (!viewerState) return;
        event.preventDefault();
        const nextScale = Math.min(
          4,
          Math.max(1, viewerState.scale - event.deltaY * 0.0018 * viewerState.scale),
        );
        zoomAt(event.clientX, event.clientY, nextScale);
      },
      { passive: false },
    );
  }

  function ensureDialog() {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'pdp-zoom';
    dialog.setAttribute('aria-label', labels.dialog);
    dialog.innerHTML = `
      <button type="button" class="pdp-zoom__close" data-zoom-close>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>
      </button>
      <button type="button" class="pdp-zoom__nav pdp-zoom__nav--prev" data-zoom-prev>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>
      </button>
      <button type="button" class="pdp-zoom__nav pdp-zoom__nav--next" data-zoom-next>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
      </button>
      <div class="pdp-zoom__viewport" data-zoom-viewport>
        <img class="pdp-zoom__img" data-zoom-img alt="" draggable="false">
      </div>
      <div class="pdp-zoom__dots" data-zoom-dots></div>
    `;
    document.body.appendChild(dialog);

    viewport = dialog.querySelector('[data-zoom-viewport]');
    zoomImage = dialog.querySelector('[data-zoom-img]');
    dots = dialog.querySelector('[data-zoom-dots]');
    previousButton = dialog.querySelector('[data-zoom-prev]');
    nextButton = dialog.querySelector('[data-zoom-next]');
    closeButton = dialog.querySelector('[data-zoom-close]');
    closeButton.setAttribute('aria-label', labels.close);
    previousButton.setAttribute('aria-label', labels.previous);
    nextButton.setAttribute('aria-label', labels.next);

    closeButton.addEventListener('click', closeZoom);
    previousButton.addEventListener('click', () => step(-1));
    nextButton.addEventListener('click', () => step(1));
    dialog.addEventListener('close', () => {
      document.body.classList.remove('pdp-zoom-locked');
      viewerState = undefined;
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1);
      }
    });

    wireGestures();
    return dialog;
  }

  function collectImages(gallery) {
    return queryAll(
      gallery,
      '.pdp-gallery__slide, .pf-gallery__slide',
    )
      .map((slide) => {
        const image = slide.querySelector('img');
        return {
          src:
            slide.getAttribute('data-zoom-src') ||
            image?.currentSrc ||
            image?.src ||
            '',
          alt: image?.alt || '',
        };
      })
      .filter((image) => image.src);
  }

  function currentIndex(gallery) {
    const activeSlide = gallery.querySelector(
      '.pdp-gallery__slide.is-active, .pf-gallery__slide.is-active',
    );
    const parsedIndex = Number.parseInt(
      activeSlide?.getAttribute('data-slide') || '0',
      10,
    );
    return Number.isNaN(parsedIndex) ? 0 : parsedIndex;
  }

  function isPreviewActive(gallery) {
    const section = gallery.closest('.pdp, .pf');
    if (section?.dataset.wizard === 'true') return true;

    const albumPreview = gallery.querySelector('.pdp-preview-layer');
    if (albumPreview?.children.length) return true;

    const framePreview = gallery.querySelector('[data-pr-preview]');
    return Boolean(framePreview && !framePreview.hasAttribute('hidden'));
  }

  function openZoom(images, startIndex, gallery) {
    if (!images.length) return;
    ensureDialog();
    viewerState = {
      images,
      index: startIndex,
      gallery,
      scale: 1,
      translateX: 0,
      translateY: 0,
    };
    renderImage();
    document.body.classList.add('pdp-zoom-locked');

    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    closeButton.focus();
  }

  function initGallery(gallery) {
    if (gallery.dataset.zoomReady) return;
    gallery.dataset.zoomReady = 'true';

    const stage = gallery.querySelector(
      '.pdp-gallery__stage, .pf-gallery__stage',
    );
    if (!stage) return;

    const zoomHint = document.createElement('button');
    zoomHint.type = 'button';
    zoomHint.className = 'pdp-zoom-hint';
    zoomHint.setAttribute('aria-label', labels.open);
    zoomHint.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 3a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm0 4v6M7 10h6"/><path d="m20 20-3.6-3.6"/></svg>';
    stage.appendChild(zoomHint);

    function refreshHint() {
      const shouldHide = isPreviewActive(gallery);
      if (zoomHint.hidden !== shouldHide) zoomHint.hidden = shouldHide;
    }

    function openGalleryZoom() {
      if (isPreviewActive(gallery)) return;
      openZoom(collectImages(gallery), currentIndex(gallery), gallery);
    }

    zoomHint.addEventListener('click', (event) => {
      event.stopPropagation();
      openGalleryZoom();
    });
    stage.addEventListener('click', (event) => {
      if (event.target.closest?.('img')) openGalleryZoom();
    });

    const section = gallery.closest('.pdp, .pf') || gallery;
    new MutationObserver(refreshHint).observe(section, {
      attributes: true,
      attributeFilter: ['hidden', 'data-wizard'],
      childList: true,
      subtree: true,
    });
    refreshHint();
  }

  function init() {
    queryAll(
      document,
      '[data-pdp-gallery], [data-pf-gallery], [data-pr-gallery]',
    ).forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
